import { useState, useEffect, useRef, useCallback } from 'react'
import Fuse from 'fuse.js'

const sdbt = require('../db/sdbt_vm21.json')
const sdbl = require('../db/sdbl_vm21.json')

// --- Corpus build (runs once at module load) ---

// Build deduped unique lines per song, parallel to sdbl
const corpus = sdbl.map(song => {
  const allLines = [
    ...(song.lyrics || []).flat(),
    ...(song.lyrics2 || []).flat(),
  ]
  const seen = new Set()
  const lines = []
  for (const raw of allLines) {
    const line = raw.trim()
    if (line && !seen.has(line.toLowerCase())) {
      seen.add(line.toLowerCase())
      lines.push(line)
    }
  }
  return { lines }
})

// Flat array of { line, songIdx } for the lyrics Fuse index
const lyricEntries = []
sdbl.forEach((song, songIdx) => {
  corpus[songIdx].lines.forEach(line => {
    lyricEntries.push({ line, songIdx })
  })
})

// --- Searching phrases ---
const SEARCHING_PHRASES = [
  'Hold fast...',
  'Stand fast...',
  'Almost there...',
  'Checking again...',
  'Behold, I knock...',
  'The song is near...',
  'Lo, I am seeking...',
  'Looking once more...',
  'Seeking the melody...',
  'Scanning the verses...',
  'Searching the songs...',
  'Turning another page...',
  'Searching the chorus...',
  'Searching diligently...',
  'The hymn draweth near...',
  'The verse approacheth...',
  'Thus saith the search...',
  'Searching the songbook...',
  'Checking the next song...',
  'Searching high and low...',
  'Verily I seek thy song...',
  'Seeking the right verse...',
  'The hymn is at the door...',
  'Even so, search quickly...',
  'Searching until the amen...',
  'Knocking upon the hymnal...',
  'Patience a little longer...',
  'The hymn revealeth itself...',
  'Seeking, and hoping to find...',
  'The verse shall soon appear...',
  'Consulting the Ancient of Days...',
  'The truth is in here somewhere...',
  'My soul waiteth for the answer...',
  'A joyful sound is being located...',
  'Prepare ye the way of the chorus...',
  'Be still; the song is on its way...',
  'For every lyric there is a season...',
  'Ask, and the verse may be revealed...',
  'It is written... somewhere in here...',
  'Knock, and the song shall be opened...',
  'Watch and wait, for the hymn cometh...',
  'Seek, and ye shall find... probably...',
  'Many are searched, but few are found...',
  'Seek, and ye may yet find the chorus...',
  'Take heart; the verse is being found...',
  'Ask, and it shall be given unto thee...',
  'Be still and know that I am searching...',
  'Where two or three verses are gathered...',
  'In due season we shall reap the chorus...',
  'Blessed are those who wait for results...',
  'Knock, and the songbook shall be opened...',
  'Arise, shine, for thy results are coming...',
  'I will lift up mine eyes unto the library...',
  'And it came to pass that a search was made...',
  'He that hath ears, let him hear the results...',
  'He leadeth me beside the still search results...',
  'Behold, I send forth a search before thy face...',
  'I searched, and I found, and I searched again...',
  'Rejoice and be glad, for the results draw near...',
  'Many songs are called, but few match the query...',
  'And a great search went out across the library...',
  'Let not thy heart be troubled; the song is coming...',
  'The harvest is plentiful but the searching is slow...',
  'Let everything that hath breath find the right song...',
  'Yea, though I walk through the valley of many songs...',
  'Fear not, for I bring you tidings of search results...',
  'Blessed are those who wait for this search to finish...',
  'The spirit is willing, but the search is still working...',
  'The harvest is plentiful, but the song is still loading...',
  'And there was silence in heaven for about half a second...',
  'Surely goodness and mercy—and the right hymn—shall follow...',
  'For everything there is a season—even finding the right hymn...',
  'In the beginning was the Word, and the Word is being searched...',
]

// --- Fuse instances ---

const titleFuse = new Fuse(sdbt, {
  keys: ['title', 'title2'],
  threshold: 0.3,
  includeScore: false,
})

const lyricFuse = new Fuse(lyricEntries, {
  keys: ['line'],
  threshold: 0.3,
  includeScore: false,
})

function SongSearchList({ onSelect, onAdd, autoFocus }) {
  const [search, setSearch] = useState('')
  const [titleResults, setTitleResults] = useState(sdbt)
  const [lyricResults, setLyricResults] = useState([])
  const [lyricsPending, setLyricsPending] = useState(false)
  const [pendingPhrase, setPendingPhrase] = useState('')
  const inputRef = useRef(null)
  const debounceRef = useRef(null)
  const phraseIntervalRef = useRef(null)
  const showPhraseTimeoutRef = useRef(null)
  const debounceDelay = useRef(100)

  const pickPhrase = useCallback((current) => {
    const others = SEARCHING_PHRASES.filter(p => p !== current)
    return others[Math.floor(Math.random() * others.length)]
  }, [])

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus()
  }, [autoFocus])

  const runLyricSearch = useCallback((query, titleMatchedSet) => {
    const t0 = performance.now()
    const raw = lyricFuse.search(query)
    const elapsed = performance.now() - t0
    debounceDelay.current = Math.max(50, elapsed * 8)

    // Group by songIdx, first match per song, exclude title-matched songs
    const seen = new Set()
    const results = []
    for (const { item } of raw) {
      const { songIdx, line } = item
      const song = sdbl[songIdx]
      if (seen.has(songIdx) || titleMatchedSet.has(song.title)) continue
      seen.add(songIdx)
      results.push({ song, matchedLine: line })
    }
    setLyricResults(results)
    setLyricsPending(false)
    setPendingPhrase('')
    if (showPhraseTimeoutRef.current) clearTimeout(showPhraseTimeoutRef.current)
    if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current)
  }, [])

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value
    setSearch(query)

    if (!query) {
      setTitleResults(sdbt)
      setLyricResults([])
      setLyricsPending(false)
      setPendingPhrase('')
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (showPhraseTimeoutRef.current) clearTimeout(showPhraseTimeoutRef.current)
      if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current)
      return
    }

    // Live title search
    const q = query.toLowerCase()
    const titleMatches = query.length < 4
      ? sdbt.filter(t => t.title.toLowerCase().includes(q) || (t.title2 && t.title2.toLowerCase().includes(q)))
      : titleFuse.search(query).map(r => r.item)
    setTitleResults(titleMatches)
    setLyricResults([])

    if (query.length < 4) {
      setLyricsPending(false)
      setPendingPhrase('')
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (showPhraseTimeoutRef.current) clearTimeout(showPhraseTimeoutRef.current)
      if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current)
      return
    }

    setLyricsPending(true)

    // Hide phrase while typing; show 1s after last keystroke, then cycle every 5s
    setPendingPhrase('')
    if (showPhraseTimeoutRef.current) clearTimeout(showPhraseTimeoutRef.current)
    if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current)
    showPhraseTimeoutRef.current = setTimeout(() => {
      setPendingPhrase(p => pickPhrase(p))
      phraseIntervalRef.current = setInterval(() => {
        setPendingPhrase(p => pickPhrase(p))
      }, 5000)
    }, 1000)
    const titleMatchedSet = new Set(titleMatches.map(t => t.title))

    // Debounced lyric search
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      runLyricSearch(query, titleMatchedSet)
    }, debounceDelay.current)
  }, [runLyricSearch, pickPhrase])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (showPhraseTimeoutRef.current) clearTimeout(showPhraseTimeoutRef.current)
    if (phraseIntervalRef.current) clearInterval(phraseIntervalRef.current)
  }, [])

  const getSong = (t) => sdbl.find(s => s.title === t.title)

  return (
    <>
      <input
        ref={inputRef}
        className="form-control fs-6 song-search-input"
        placeholder="Search..."
        value={search}
        onChange={handleSearchChange}
      />
      <div className="song-search-list">
        {titleResults.map((t, i) => (
          <div
            key={`t-${i}`}
            className="song-search-item frosted ticks"
            onClick={() => { const full = getSong(t); if (full && onSelect) onSelect(full); }}
          >
            <div className="song-search-item-text">
              <div className="fg-l2 fs-6">{t.title}</div>
              {t.title2 && <div className="fg-l1 fw-lighter fs-6">{t.title2}</div>}
            </div>
            {onAdd && (
              <button
                className="song-search-add-btn"
                onClick={e => { e.stopPropagation(); const full = getSong(t); if (full) onAdd(full); }}
              >+</button>
            )}
          </div>
        ))}
        {lyricResults.map(({ song, matchedLine }, i) => (
          <div
            key={`l-${i}`}
            className="song-search-item frosted ticks"
            onClick={() => { if (onSelect) onSelect(song); }}
          >
            <div className="song-search-item-text">
              <div className="fg-l2 fs-6">{song.title}</div>
              {song.title2 && <div className="fg-l1 fw-lighter fs-6">{song.title2}</div>}
              <div className="song-search-item-snippet-divider" />
              <div className="song-search-item-snippet fg-l1">{matchedLine}</div>
            </div>
            {onAdd && (
              <button
                className="song-search-add-btn"
                onClick={e => { e.stopPropagation(); onAdd(song); }}
              >+</button>
            )}
          </div>
        ))}
        {search && (lyricsPending || (titleResults.length === 0 && lyricResults.length === 0)) && (
          <div className="song-search-status">
            {lyricsPending
              ? <span className="song-search-status-pending">{pendingPhrase}</span>
              : <span className="song-search-status-empty">no results</span>
            }
          </div>
        )}
      </div>
    </>
  )
}

export default SongSearchList
